import React from 'react';
import { Box, Typography, Button, useMediaQuery } from '@mui/material';
import { Menu } from 'lucide-react';
import UserMenu from './user-menu';
import { useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useLogoutContext } from '../providers/logout-context';
import { endServerSession } from '../util/session';
import { useSidebarMobile } from '../providers/sidebar-mobile-context';
import SidebarMobileDrawer from './sidebar-mobile-drawer';

interface TopbarProps {
    children?: React.ReactNode;
    userImageUrl?: string;
    userName?: string;
    title?: React.ReactNode;
    // Shown right after the title (e.g. page-wide totals).
    titleAdornment?: React.ReactNode;
    // Optional override - most pages should just rely on isMobile below and
    // let Topbar render (and wire up) the hamburger button itself, since
    // relying on every page to build and pass its own was how several pages
    // ended up with no way to open the sidebar on mobile at all.
    mobileMenuButton?: React.ReactNode;
    isMobile?: boolean;
}

const Topbar: React.FC<TopbarProps> = ({ children, userImageUrl, userName, title, titleAdornment, mobileMenuButton, isMobile }) => {
    const navigate = useNavigate();
    // The admin portal keeps its own dark header - not a company's colour scheme.
    const isAdmin = useLocation().pathname.startsWith('/admin');
    const titleColor = isAdmin ? '#fff' : 'var(--color-default-text)';
    const { initiateLogout } = useLogoutContext();
    const { sidebarOpen, setSidebarOpen } = useSidebarMobile();
    // Detected independently of the isMobile prop - several pages never
    // compute/pass it, which left them with no way to reach the sidebar at
    // all on mobile (the desktop Sidebar is unconditionally hidden below
    // this width in index.tsx). Matches that same breakpoint.
    const isMobileViewport = useMediaQuery('(max-width:600px)');

    // Falls back to a default hamburger button whenever a page hasn't built
    // its own - so the sidebar stays reachable on mobile even on pages that
    // never explicitly opted in. Pages that do pass mobileMenuButton (even
    // explicitly `false`/`null` to suppress it) keep full control.
    const resolvedMobileMenuButton = mobileMenuButton !== undefined
        ? mobileMenuButton
        : (isMobileViewport && (
            <Button
                variant="contained"
                color="primary"
                onClick={() => setSidebarOpen(true)}
                sx={{
                    color: '#fff',
                    height: 40,
                    display: 'flex',
                    gap: 1,
                    minWidth: 40,
                    p: 1,
                    backgroundColor: 'var(--color-primary)',
                    '&:hover': { backgroundColor: 'var(--color-primary)' },
                }}
            >
                <Menu size={20} color="#fff" />
            </Button>
        ));

    const handleLogout = () => {
        initiateLogout();
        // Ends the session on the server too - before the cookie holding the token goes.
        endServerSession();
        Cookies.remove('token');
        Cookies.remove('user');
        Cookies.remove('entities');
        navigate('/auth/login');
    };

    return (
        <>
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                height: 'fit-content',
                minHeight: 65,
                px: isMobile ? 2 : 3,
                py: isMobile ? 2 : 1,
                background: isAdmin ? '#323232' : 'var(--color-page-background)',
                borderBottom: isAdmin ? '1px solid #262626' : '1px solid #f0f1f3',
                boxSizing: 'border-box',
                position: 'sticky',
                top: 0,
                zIndex: 11,
            }}
        >
            {(children && title) ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: isMobile ? 1 : 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {resolvedMobileMenuButton}
                        <Typography fontWeight={isMobile ? 600 : 500 } pl={isMobile ? 0 : 3} variant="h6" sx={{ color: titleColor }}>
                        {title}
                        </Typography>
                        {titleAdornment && <Box sx={{ ml: 2 }}>{titleAdornment}</Box>}
                    </Box>
                    <Box>
                        {children}
                    </Box>
                </Box>
                ) : children ? (
                <>{children}</>
                ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 260, gap: 1 }}>
                    {resolvedMobileMenuButton}
                    {title && (
                    <Typography fontWeight={isMobile ? 600 : 500 } pl={isMobile ? 0 : 3} variant="h6" sx={{ color: titleColor }}>
                        {title}
                    </Typography>
                    )}
                    {titleAdornment && <Box sx={{ ml: 2 }}>{titleAdornment}</Box>}
                </Box>
            )}
            <UserMenu
                onAccount={() => navigate('/settings/profile')}
                onLogout={handleLogout}
            />
        </Box>
        {/* Rendered here rather than left to each page - the drawer itself
            is a no-op in the DOM while closed, and centralizing it means
            the hamburger button above always has something to open even on
            a page that never set this up itself. Pages that already render
            their own end up with a second (harmless, identically-driven by
            the same shared sidebarOpen state) instance. */}
        <SidebarMobileDrawer
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
        />
        </>
    );
};

export default Topbar;
